import React, { useState, useEffect } from 'react';
import { Play, Users, Trophy, Eye, CheckCircle } from 'lucide-react';
import { MatchplayFormat, CreatePlayerRequest, MatchplayPlayer } from '../../../types/matchplay-v2';
import { MatchplayDatabase } from '../../../lib/matchplay-v2/database';

interface MatchGenerationProps {
  tournamentId: string;
  format: MatchplayFormat;
  players: CreatePlayerRequest[];
  onGenerateMatches: () => void;
  isLoading: boolean;
}

interface MatchPreview {
  matchNumber: number;
  team1: string[];
  team2: string[];
  format: MatchplayFormat;
}

export function MatchGeneration({ 
  tournamentId, 
  format, 
  players, 
  onGenerateMatches, 
  isLoading 
}: MatchGenerationProps) {
  const [matchPreviews, setMatchPreviews] = useState<MatchPreview[]>([]);
  const [tournamentPlayers, setTournamentPlayers] = useState<MatchplayPlayer[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(true);

  // Load players from database to get their IDs
  useEffect(() => {
    const loadPlayers = async () => {
      try {
        const dbPlayers = await MatchplayDatabase.getPlayers(tournamentId);
        setTournamentPlayers(dbPlayers);
        generateMatchPreviews(dbPlayers);
      } catch (error) {
        console.error('Error loading players:', error);
      }
    };

    loadPlayers();
  }, [tournamentId]);

  const generateMatchPreviews = (dbPlayers: MatchplayPlayer[]) => {
    const previews: MatchPreview[] = [];

    if (format === 'singles') {
      // Generate singles matches
      for (let i = 0; i < dbPlayers.length; i += 2) {
        if (i + 1 < dbPlayers.length) {
          previews.push({
            matchNumber: Math.floor(i / 2) + 1,
            team1: [dbPlayers[i].playerName],
            team2: [dbPlayers[i + 1].playerName],
            format
          });
        }
      }
    } else {
      // Generate team matches (fourball/foursomes)
      const teamA = dbPlayers.filter(p => p.teamAssignment === 'A');
      const teamB = dbPlayers.filter(p => p.teamAssignment === 'B');
      
      // Check if players have match numbers assigned
      const hasMatchNumbers = teamA.some(p => p.matchNumber) || teamB.some(p => p.matchNumber);
      
      if (hasMatchNumbers) {
        // Use match numbers to group players
        const matchNumbers = Array.from(new Set([
          ...teamA.filter(p => p.matchNumber).map(p => p.matchNumber!),
          ...teamB.filter(p => p.matchNumber).map(p => p.matchNumber!)
        ])).sort((a, b) => a - b);
        
        matchNumbers.forEach(matchNum => {
          const teamAPlayers = teamA.filter(p => p.matchNumber === matchNum);
          const teamBPlayers = teamB.filter(p => p.matchNumber === matchNum);
          
          if (teamAPlayers.length >= 2 && teamBPlayers.length >= 2) {
            previews.push({
              matchNumber: matchNum,
              team1: [teamAPlayers[0].playerName, teamAPlayers[1].playerName],
              team2: [teamBPlayers[0].playerName, teamBPlayers[1].playerName],
              format
            });
          }
        });
      } else {
        // Fallback to sequential pairing if no match numbers
        for (let i = 0; i < Math.min(teamA.length, teamB.length); i += 2) {
          if (i + 1 < teamA.length && i + 1 < teamB.length) {
            previews.push({
              matchNumber: Math.floor(i / 2) + 1,
              team1: [teamA[i].playerName, teamA[i + 1].playerName],
              team2: [teamB[i].playerName, teamB[i + 1].playerName],
              format
            });
          }
        }
      }
    }

    setMatchPreviews(previews);
  };

  // Removed shuffle functionality - admin controls match setup

  const getFormatDisplayName = (format: MatchplayFormat) => {
    switch (format) {
      case 'singles': return 'Singles';
      case 'fourball': return 'Four Ball (Best Ball)';
      case 'foursomes': return 'Foursomes (Alternate Shot)';
    }
  };

  const getTeamStats = () => {
    if (format === 'singles') {
      return {
        totalPlayers: tournamentPlayers.length,
        totalMatches: matchPreviews.length,
        playersPerMatch: 2
      };
    } else {
      const teamA = tournamentPlayers.filter(p => p.teamAssignment === 'A');
      const teamB = tournamentPlayers.filter(p => p.teamAssignment === 'B');
      
      return {
        totalPlayers: tournamentPlayers.length,
        teamA: teamA.length,
        teamB: teamB.length,
        totalMatches: matchPreviews.length,
        playersPerMatch: 4
      };
    }
  };

  const stats = getTeamStats();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Generate Matches</h2>
        <p className="text-gray-600">
          Review the match pairings and start your {getFormatDisplayName(format)} tournament
        </p>
      </div>

      {/* Tournament Summary */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-primary-900">Tournament Summary</h3>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-primary-600">{stats.totalPlayers}</div>
            <div className="text-sm text-primary-700">Total Players</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-600">{stats.totalMatches}</div>
            <div className="text-sm text-primary-700">Total Matches</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-600">{getFormatDisplayName(format)}</div>
            <div className="text-sm text-primary-700">Format</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-600">{stats.playersPerMatch}</div>
            <div className="text-sm text-primary-700">Players per Match</div>
          </div>
        </div>

        {format !== 'singles' && (
          <div className="mt-4 pt-4 border-t border-primary-200">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-blue-600">{stats.teamA}</div>
                <div className="text-sm text-blue-700">Team A Players</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-red-600">{stats.teamB}</div>
                <div className="text-sm text-red-700">Team B Players</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Match Pairings</h3>
          {format !== 'singles' && (
            <p className="text-sm text-gray-600 mt-1">
              {tournamentPlayers.some(p => p.matchNumber) 
                ? 'Using CSV match numbers for pairings' 
                : 'Using automatic sequential pairings'
              }
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            <Eye className="w-4 h-4" />
            {isPreviewMode ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
      </div>

      {/* Match Preview */}
      <div className="space-y-4">
        {matchPreviews.map((match) => (
          <div key={match.matchNumber} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 text-primary-600 rounded-full font-semibold">
                  {match.matchNumber}
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Team 1 */}
                  <div className="flex items-center gap-2">
                    {format !== 'singles' && (
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    )}
                    <div className="font-medium">
                      {match.team1.join(' / ')}
                    </div>
                  </div>
                  
                  <span className="text-gray-400 font-medium">vs</span>
                  
                  {/* Team 2 */}
                  <div className="flex items-center gap-2">
                    {format !== 'singles' && (
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                    )}
                    <div className="font-medium">
                      {match.team2.join(' / ')}
                    </div>
                  </div>
                </div>
              </div>

              {isPreviewMode && (
                <div className="text-sm text-gray-500">
                  {getFormatDisplayName(format)}
                </div>
              )}
            </div>

            {isPreviewMode && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Team 1:</span>
                    <ul className="mt-1 space-y-1">
                      {match.team1.map((player, index) => {
                        const playerData = tournamentPlayers.find(p => p.playerName === player);
                        return (
                          <li key={index} className="flex justify-between">
                            <span>{player}</span>
                            <span className="text-gray-400">HC: {playerData?.handicap}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div>
                    <span className="font-medium">Team 2:</span>
                    <ul className="mt-1 space-y-1">
                      {match.team2.map((player, index) => {
                        const playerData = tournamentPlayers.find(p => p.playerName === player);
                        return (
                          <li key={index} className="flex justify-between">
                            <span>{player}</span>
                            <span className="text-gray-400">HC: {playerData?.handicap}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {matchPreviews.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No matches can be generated with the current player setup.</p>
          <p className="text-sm mt-1">Please ensure you have the correct number of players and team assignments.</p>
        </div>
      )}

      {/* Format-specific instructions */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Tournament Rules</h4>
        <div className="text-sm text-gray-600 space-y-1">
          {format === 'singles' && (
            <>
              <p>• Each match is head-to-head between two players</p>
              <p>• Players compete hole-by-hole using match play scoring</p>
              <p>• Winner is determined by holes won, not total strokes</p>
            </>
          )}
          {format === 'fourball' && (
            <>
              <p>• Each player plays their own ball throughout the round</p>
              <p>• Team score is the lower of the two players' scores on each hole</p>
              <p>• Teams compete hole-by-hole using match play scoring</p>
            </>
          )}
          {format === 'foursomes' && (
            <>
              <p>• Partners alternate hitting the same ball</p>
              <p>• One player tees off on odd holes, partner on even holes</p>
              <p>• Teams compete hole-by-hole using match play scoring</p>
            </>
          )}
          <p>• Matches can end early if one side cannot mathematically catch up</p>
          <p>• Results are displayed as holes up/down (e.g., "3&2", "AS", "1U")</p>
        </div>
      </div>

      {/* Final Actions */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Back to Players
        </button>
        
        <button
          onClick={onGenerateMatches}
          disabled={isLoading || matchPreviews.length === 0}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Generating Tournament...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Start Tournament
            </>
          )}
        </button>
      </div>
    </div>
  );
} 